/**
 * Die Seiten der Einstellungen — Namen, Gruppen, Stichworte. (02.10.2026, Ticket 2914)
 *
 * Bis hierher stand die Liste in frontend/lib/settings-view.ts. Seit Vespera
 * Einstellungen auf Zuruf ändern kann (tools/einstellung-aendern.ts), braucht
 * auch der Server sie: Er nennt bei einer Absage den Weg („Einstellungen →
 * Mitteilungen“). settings-view.ts reicht sie unverändert weiter.
 * ⚠️ Eine Liste, ein Ort: wer eine Seite umbenennt oder Stichworte ergänzt,
 *    ändert sie HIER.
 */
/*
 * ⚠️⚠️ 23.09.2026 — EINE ÜBERSICHT STATT DREIER REITER.
 *
 * Martin: *„Das persönliche Vokabular in den Einstellungen ist so total
 * unbrauchbar … eine ewig lange Liste … bitte mach einen Vorschlag, so wie
 * vorhin mit der Zuhause-Seite."* Den Entwurf „Einstellungen neu geordnet"
 * hat er mit *„sieht toll aus!! Bitte das genau so umsetzen!"* freigegeben.
 *
 * Gemessen waren es drei Reiter — „Ich" 3.956, „Stimme" 4.867, „Mehr" 4.340
 * Punkte hoch, „Mehr" mit 19 Abschnitten untereinander. Jetzt EINE Übersicht:
 * jede Zeile zeigt ihren Stand und führt auf eine kurze eigene Seite. Die
 * Karten darin sind dieselben wie vorher — sie wandern, sie werden nicht neu
 * erfunden.
 */
export type SeitenId =
  | "profil"
  | "persoenlichkeit"
  | "deinedaten"
  | "sprechen"
  | "stimme"
  | "woerter"
  | "tagesaufnahme"
  | "stimmprobe"
  | "verlauf"
  | "schwerpunkte"
  | "waechter"
  | "wissen"
  | "mitteilungen"
  | "darstellung"
  | "verbindungen"
  | "modelle"
  | "entwickler"
  | "versuche";

type GruppenId = "du" | "sprechen" | "aufzeichnen" | "vonsich" | "geraete" | "team" | "erweitert";

export const EINST_GRUPPEN: ReadonlyArray<{ id: GruppenId; titel: string }> = [
  { id: "du", titel: "Du" },
  { id: "sprechen", titel: "Mikro und Stimme" },
  { id: "aufzeichnen", titel: "Aufzeichnen und Verlauf" },
  { id: "vonsich", titel: "Was Vespera von sich aus tut" },
  { id: "geraete", titel: "App, Geräte und Konto" },
  // 17.10.2026 (Einstellungen neu ordnen, B4): das Team und die Versuche haben je eine eigene Gruppe, ganz unten.
  { id: "team", titel: "Entwicklung" },
  { id: "erweitert", titel: "Versuche und Technik" },
];

export interface EinstSeite {
  id: SeitenId;
  titel: string;
  gruppe: GruppenId;
  /** Zeichen aus lib/zeichen.ts — nur in den ersten drei Gruppen (Entwurf). */
  zeichen?: string;
  /** Wörter, unter denen die Suche die Seite auch findet. */
  stichworte: string;
}

export const EINST_SEITEN: ReadonlyArray<EinstSeite> = [
  { id: "profil", titel: "Profil", gruppe: "du", stichworte: "name adresse geburtstag telefon bild" },
  { id: "persoenlichkeit", titel: "Persönlichkeit", gruppe: "du", zeichen: "persona", stichworte: "persona human design emojis bot name stil ausrichtung selbstermächtigung innen außen haltungsbaustein" },
  // 17.10.2026 („Deine Daten“, Bahn B): Export, Wo liegt was, Löschen. Keine Wörter „verlauf“, „archiv“, „chat“ — die bleiben beim Verlauf.
  { id: "deinedaten", titel: "Deine Daten", gruppe: "du", zeichen: "dokument", stichworte: "export exportieren herunterladen download mitnehmen kopie sicherung löschen papierkorb speicher speicherplatz wo liegt was auskunft dsgvo datenschutz konto löschen" },
  { id: "sprechen", titel: "Sprechtaste und „Hey Vespera“", gruppe: "sprechen", zeichen: "mikro", stichworte: "wachwort hey vespera frage absenden stille auslösewort mikro anruf modell gemini" },
  { id: "stimme", titel: "Stimme und Vorlesen", gruppe: "sprechen", zeichen: "klang", stichworte: "stimme vorlesen tempo aoede charon rückfall musik lied lieder probestück probe" },
  { id: "woerter", titel: "Eigene Wörter", gruppe: "sprechen", zeichen: "woerter", stichworte: "vokabular begriffe namen aussprache" },
  { id: "stimmprobe", titel: "Deine Stimmprobe", gruppe: "sprechen", zeichen: "person", stichworte: "erkennung sprecher stimme referenz stimmung" },
  { id: "tagesaufnahme", titel: "Tagesaufnahme", gruppe: "aufzeichnen", zeichen: "aufzeichnen", stichworte: "immer aufzeichnen durchgehend alltag" },
  { id: "verlauf", titel: "Verlauf", gruppe: "aufzeichnen", zeichen: "erinnerung", stichworte: "archiv tage chat kürzen ausblenden" },
  { id: "schwerpunkte", titel: "Schwerpunkte", gruppe: "vonsich", stichworte: "themen fokus" },
  { id: "waechter", titel: "Achtsamkeits-Wächter", gruppe: "vonsich", stichworte: "achtsamkeit signal" },
  { id: "wissen", titel: "Passives Wissen", gruppe: "vonsich", stichworte: "wissensmodul recherche" },
  { id: "mitteilungen", titel: "Mitteilungen", gruppe: "vonsich", stichworte: "push benachrichtigungen anruf anrufen ruft an ruhezeit" },
  { id: "darstellung", titel: "Darstellung", gruppe: "geraete", stichworte: "farbe hell dunkel palette wald salbei zuhause morse menü jahreszeit frühling sommer herbst winter szene" },
  { id: "verbindungen", titel: "Verbindungen", gruppe: "geraete", stichworte: "kalender ical browser erweiterung mac desktop standort coach blender einzelfreigabe" },
  // 23.09.2026, Ticket 2516: „fable" aus den Stichworten — es gibt dort nichts mehr zu finden.
  // 25.10.2026 (Martin: „Warum habe ich ‚Modelle‘ und ‚Konten‘? … verwirrend“): „Modelle“ und „Konto und Version“ sind EINE Seite.
  // Der alte Deeplink „konto“ führt per resolveSubTab hierher. ⚠️ Stichworte beider Seiten — `ortFuer` und die Suche finden beides hier.
  { id: "modelle", titel: "Modelle und Konto", gruppe: "geraete", stichworte: "opus sonnet claude gemini denktiefe antworten modell zugang schlüssel abo abmelden version zurücksetzen claude konto" },
  /*
   * 16.10.2026 (Einstellungen neu ordnen, B2/B3 — Martin, 20:16: „Ja, du sollst bauen“): zwei neue Seiten, seit B4 (17.10.2026) in je
   * einer eigenen Gruppe („Entwickler-Team“, „Erweitert“) unter den übrigen.
   *   - „Entwickler-Team“: Schmied und Zimmermann an EINEM Ort (bis dahin unter „Stimme und Vorlesen“ → „Entwicklung / Test“).
   *     Nur Konto 1 sieht die Zeile (settings-view.ts `seiteSichtbar`).
   *   - „Erweitert“: die Versuchsschalter, jeder eingeklappt und als „Versuch“ markiert.
   * ⚠️ Keine allgemeinen Wörter wie „stimme“ oder „vorlesen“ in den Stichworten: `ortFuer` (shared/einstellung-aendern.ts)
   *    wählt über sie den Weg einer Absage — die Stimme bleibt unter „Stimme und Vorlesen“.
   */
  { id: "entwickler", titel: "Entwickler-Team", gruppe: "team", stichworte: "schmied zimmermann hüter entwickler dreier modus mitlesen mitschreiben team" },
  { id: "versuche", titel: "Erweitert", gruppe: "erweitert", stichworte: "versuch versuche test beta experimente nativer vorleser datenstrom schneller ton echtzeit bildrate ruckeln" },
];
